import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from './../Glob';
import { config } from '../config';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { Constants } from '../../../../_common/platform/ts/Constants';
import { apiCall } from '../../../../_common/platform/ts/AqlUtility';
import * as Utilities from '../../../../_common/platform/ts/Utilities';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';

export interface IDirsData {
    [instanceId: string]: IDirsInstanceState;
}

export interface IDirsState {
    data: IDirsData;
}

export interface IDirsDefs {
    content?: JQuery.Deferred<any>;
}

export interface IDirItem {
    dirid?: number;
    cid?: number;
    name: string;
    parent?: IDirItem;
    subdirlist?: IDirItem[];
    parentId?: number;

    back?: boolean;
    nomark?: boolean;
}

export interface IRenameLoading {
    [id: number]: boolean;
}

export interface IDirsInstanceData {
    dirs: any[];
    dirsHash: {
        [id: number]: IDirItem;
    };
}

export interface IDirsInstanceState {
    loading: boolean;

    renameLoading: IRenameLoading;
    rename: number;

    errors: any[];

    data: IDirsInstanceData;

    _defs: IDirsDefs;
}

export class DirsStore extends BaseStore<IDirsState, AppActions, Dispatcher, IGlob> {
    public static getInitialState() {
        return {
            data: {}
        };
    }

    private static getInitialInstanceState(): IDirsInstanceState {
        return {
            loading: false,
            renameLoading: {},

            errors: [],

            data: {
                dirs: [],
                dirsHash: {}
            },

            rename: null,

            _defs: {}
        };
    }

    public _state: IDirsState;
    public static _name = 'dirs-store';

    private cleanCache() {
        this._state.data = {};
    }

    public getInstanceState(instanceId: string): IDirsInstanceState {
        let state = this._state.data[instanceId];
        if (state == null) {
            state = this._state.data[instanceId] = DirsStore.getInitialInstanceState();
        }

        return state;
    }

    public getInstanceData(instanceId: string): IDirsInstanceState {
        return this._state.data[instanceId];
    }

    private getTreeArrItem(item: IDirItem, parent: IDirItem) {
        item.parent = parent;
        item.subdirlist = Utilities.sortByName(item.subdirlist);

        return [item].concat(this.getTreeArr(item.subdirlist, item));
    }

    private getTreeArr(arr: IDirItem[], parent: IDirItem) {
        return Array.prototype.concat.apply(
            [],
            _.map(arr, (item) => {
                return this.getTreeArrItem(item, parent);
            })
        );
    }

    private getSortedDirs(arrOrigin: IDirItem[]) {
        return Utilities.sortByName(arrOrigin);
    }

    private getTreeHash(rootDirId, rootName: string, arrOrigin) {
        const arr: any[] = this.getTreeArrItem(
            {
                dirid: rootDirId,
                name: rootName,
                subdirlist: arrOrigin
            },
            null
        );

        const hash = {};

        _.each(arr, (item) => {
            hash[item.dirid] = item;
        });

        return hash;
    }

    private contentLoad(
        instanceId: string,
        rootDirId: number = config.WIDGETS_ROOT_DIR_ID,
        rootName = config.WIDGETS_ROOT_DIR_NAME,
        force = false,
        silent = false
    ) {
        const defContent = $.Deferred<any>();

        let instanceState = this._state.data[instanceId];

        if (instanceState == null) {
            instanceState = this._state.data[instanceId] = DirsStore.getInitialInstanceState();
        }

        if (instanceState._defs.content != null && instanceState._defs.content.state() === 'pending') {
            return;
        }

        instanceState._defs.content = defContent;

        if (instanceState.data.dirs.length !== 0 && force !== true) {
            return;
        }

        if (silent !== true) {
            instanceState.loading = true;
        }

        const def = this.getSubDirTree(rootDirId);

        def.done((resp: any) => {
            instanceState.data = {
                dirs: this.getSortedDirs(resp.result),
                dirsHash: this.getTreeHash(rootDirId, rootName, resp.result)
            };

            defContent.resolve(resp);
        })
            .fail((resp, respHome) => {
                instanceState.errors = [resp.error?.message || resp.result?.message];

                defContent.reject(resp);
            })
            .always((resp, respHome) => {
                this.actions.dirsContentLoadFinish(instanceId);
            });
    }

    private userGroupContentLoad(
        instanceId: string,
        rootDirId: number = -1,
        rootName = 'Group Root',
        force = false,
        silent = false
    ) {
        const defContent = $.Deferred<any>();

        let instanceState = this._state.data[instanceId];
        if (instanceState == null) {
            instanceState = this._state.data[instanceId] = DirsStore.getInitialInstanceState();
        }

        if (instanceState._defs.content != null && instanceState._defs.content.state() === 'pending') {
            return;
        }

        instanceState._defs.content = defContent;

        if (instanceState.data.dirs.length !== 0 && force !== true) {
            return;
        }

        if (silent !== true) {
            instanceState.loading = true;
        }

        const def = this.getGroupTree(rootDirId);

        def.done((resp: any) => {
            let groups = _.map(resp.result, (group) => {
                return {
                    description: group.description,
                    name: group.displayName,
                    dirid: group.groupId,
                    owner: group.owner
                };
            });
            instanceState.data = {
                dirs: this.getSortedDirs(groups),
                dirsHash: this.getTreeHash(rootDirId, rootName, groups)
            };

            defContent.resolve(resp);
        })
            .fail((resp, respHome) => {
                instanceState.errors = [resp.error?.message || resp.result?.message];

                defContent.reject(resp);
            })
            .always((resp, respHome) => {
                this.actions.dirsUserGroupContentLoadFinish(instanceId);
            });
    }

    public getSubDirTree(rootDirID: number) {
        return apiCall(this.actions, 'dir_GetSubDirTree', {
            dirid: rootDirID
        });
    }

    private getGroupTree(rootDirId) {
        return this.glob.AQLQueries.callMethod('user_getAllGroups', {});
    }

    private contentLoadFinish(instanceId: string) {
        const instanceState = this._state.data[instanceId];

        if (instanceState == null) {
            return;
        }

        instanceState.loading = false;
    }

    private groupContentLoadFinish(instanceId: string) {
        const instanceState = this._state.data[instanceId];
        if (instanceState == null) {
            return;
        }

        instanceState.loading = false;
    }

    private renameStart(id: number, instanceID: string) {
        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        instanceState.rename = id;
    }

    private renameEnd(id: number, value: string, instanceID: string) {
        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        instanceState.rename = null;

        const dirItem = instanceState.data.dirsHash[id];
        if (dirItem == null) {
            return;
        }

        const def = $.Deferred<any>();
        const prevName = dirItem.name;

        dirItem.name = value;

        if (dirItem.dirid != null) {
            instanceState.renameLoading[dirItem.dirid] = true;

            apiCall(
                this.actions,
                'dir_Rename',
                {
                    name: value,
                    dirid: dirItem.dirid
                },
                null,
                def
            );

            def.done((resp) => {
                //Rename the same folder in other instances.
                _.each(this._state.data, (state, stateInstanceID) => {
                    if (stateInstanceID !== instanceID) {
                        const stateDirItem = state.data.dirsHash[id];

                        if (stateDirItem != null) {
                            stateDirItem.name = value;

                            this.actions.dirsRenameFinish(stateDirItem.dirid, value, stateInstanceID, false);
                        }
                    }
                });

                this.actions.showNotification_Success('Folder was succesfully renamed');
            })
                .fail((resp) => {
                    dirItem.name = prevName;
                })
                .always(() => {
                    this.actions.dirsRenameFinish(dirItem.dirid, dirItem.name, instanceID, false);
                });
        } else if (dirItem.cid != null && dirItem.parentId != null) {
            instanceState.renameLoading[dirItem.cid] = true;

            apiCall(
                this.actions,
                'dir_Create',
                {
                    name: value,
                    parent: dirItem.parentId
                },
                null,
                def
            );

            def.done((resp) => {
                dirItem.dirid = resp.result;
                instanceState.data.dirsHash[dirItem.dirid] = dirItem;

                //Add the same folder to other instances.
                _.each(this._state.data, (state, stateInstanceID) => {
                    if (stateInstanceID !== instanceID) {
                        if (this.createNewDirItem(state, dirItem.parentId, dirItem.name, dirItem.cid, dirItem.dirid)) {
                            this.actions.dirsRenameFinish(dirItem.dirid, dirItem.name, stateInstanceID, true);
                        }
                    }
                });

                this.actions.showNotification_Success('Folder "' + value + '" was successfully created');
            })
                .fail((resp) => {
                    this.removeDirItem(dirItem, instanceState.data);
                })
                .always(() => {
                    delete instanceState.data.dirsHash[dirItem.cid];

                    this.actions.dirsRenameFinish(dirItem.dirid, dirItem.name, instanceID, true);
                });
        }
    }

    removeDirItem(dirItem: IDirItem, data: IDirsInstanceData) {
        if (dirItem == null) {
            return;
        }

        let subDirs: any[];

        if (dirItem.parent == null) {
            subDirs = data.dirs;
        } else {
            subDirs = dirItem.parent.subdirlist;

            const dirs = data.dirs;

            if (_.indexOf(dirs, dirItem) >= 0) {
                dirs.splice(_.indexOf(dirs, dirItem), 1);
            }
        }

        if (_.indexOf(subDirs, dirItem) >= 0) {
            subDirs.splice(_.indexOf(subDirs, dirItem), 1);
        }
    }

    createNewDirItem(instanceState: IDirsInstanceState, parentID: number, name: string, cid: number, id?: number) {
        if (instanceState == null) {
            return false;
        }

        const { data } = instanceState;

        const parent = data.dirsHash[parentID];
        if (parent == null) {
            return false;
        }

        if (parent.subdirlist == null) {
            parent.subdirlist = [];
        }

        const obj: IDirItem = {
            parentId: parentID,
            parent: parent,
            name: name,
            subdirlist: []
        };

        if (id == null) {
            obj.cid = cid;
            data.dirsHash[cid] = obj;
        } else {
            obj.dirid = id;
            data.dirsHash[id] = obj;
        }

        parent.subdirlist.push(obj);

        // if we want insert in root folder
        if (parent.parent == null) {
            data.dirs.push(obj);
        }

        return true;
    }

    private createStart(parentID: number, instanceID: string) {
        const instanceState = this._state.data[instanceID];
        const cid = Utilities.getNewID();

        if (this.createNewDirItem(instanceState, parentID, 'New folder', cid)) {
            this.actions.dirsRenameStart(cid, instanceID);
        }
    }

    private renameFinish(id: number, name: string, instanceID: string, isNew: boolean) {
        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        const { data } = instanceState;
        const dirItem = data.dirsHash[id];

        delete instanceState.renameLoading[id];

        if (dirItem == null) {
            return;
        }

        if (dirItem.cid != null) {
            delete data.dirsHash[dirItem.cid];
            data.dirsHash[dirItem.dirid] = dirItem;
        }

        //IL: sort folders
        const dir = data.dirsHash[dirItem.dirid];

        if (dir != null && dir.parent != null && dir.parent.subdirlist != null) {
            dir.parent.subdirlist = Utilities.sortByName(dir.parent.subdirlist);
        }

        data.dirs = Utilities.sortByName(data.dirs);

        if (data.dirs[0] && data.dirs[0].parent) {
            const find = this.findRecursive(data.dirs[0].parent, function (obj) {
                return obj.dirid === id;
            });

            if (find != null && find.parent != null && find.parent.subdirlist != null) {
                find.parent.subdirlist = Utilities.sortByName(find.parent.subdirlist);
            }
        }
    }

    private delete(id: number, instanceId: string, deleteSubItemsToo: boolean) {
        const instanceState = this._state.data[instanceId];
        if (instanceState == null) {
            return;
        }

        // VR: it is not needed more:
        const obj = instanceState.data.dirsHash[id];
        if (deleteSubItemsToo === false && (obj == null || obj.subdirlist.length > 0)) {
            this.actions.showNotification_Warning('Warning: the folder "' + id + '" isn\'t empty');
            return;
        }

        const def = $.Deferred<any>();

        instanceState.rename = null;
        instanceState.renameLoading[id] = true;

        apiCall(
            this.actions,
            'dir_Delete',
            {
                dirid: id,
                deleteNonEmpty: deleteSubItemsToo
            },
            null,
            def
        );

        def.done((resp) => {
            const data = resp.result;

            if (data != null && data['code'] !== undefined && data['message'] !== undefined) {
                this.actions.showNotification_Fail(data['message']);
                this.actions.dirsDeleteFinish(false, id, instanceId);
            } else {
                this.actions.showNotification_Success('Folder "' + id + '" was deleted');
                this.actions.dirsDeleteFinish(true, id, instanceId);
            }
        }).fail((resp) => {
            this.actions.dirsDeleteFinish(false, id, instanceId);
        });
    }

    private deleteFinish(success: boolean, id: number, instanceID: string) {
        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        delete instanceState.renameLoading[id];

        if (success !== true) {
            return;
        }

        //Delete the same folder in all instances.
        _.each(this._state.data, (state, stateInstanceID) => {
            const { data } = state;
            const dirItem = data.dirsHash[id];

            delete data.dirsHash[id];

            this.removeDirItem(dirItem, data);
        });
    }

    private findRecursive(obj, func: (obj) => boolean) {
        if (obj == null) {
            return;
        }

        if (func(obj)) {
            return obj;
        }

        return this.findRecursiveArr(obj.subdirlist, func);
    }

    private findRecursiveArr(arr, func: (obj) => boolean) {
        let find = _.find(arr, func);

        if (find != null) {
            return find;
        }

        var i = 0;
        var l = arr.length;

        while (i < l && find == null) {
            find = this.findRecursive(arr[i], func);

            i += 1;
        }

        return find;
    }

    private move(id, parentID, instanceID: string, rootDirID = config.WIDGETS_ROOT_DIR_ID) {
        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        const dirItem = instanceState.data.dirsHash[id];

        if (dirItem.parent == null) {
            return;
        } else if (dirItem.parent.dirid === parentID) {
            return;
        } else if (id === parentID) {
            return;
        }

        const find = this.findRecursive(dirItem, (obj) => {
            return obj.dirid === parentID;
        });

        if (find != null) {
            return;
        }

        const def = $.Deferred<any>();

        instanceState.renameLoading[id] = true;

        apiCall(
            this.actions,
            'dir_Move',
            {
                dirid: id,
                newParent: parentID
            },
            null,
            def
        );

        def.done(() => {
            this.actions.dirsMoveFinish(true, id, parentID, instanceID, rootDirID);
            this.actions.showNotification_Success('Folder "' + id + '" was successfully moved to "' + parentID + '"');
        }).fail((obj) => {
            this.actions.dirsMoveFinish(false, id, parentID, instanceID, rootDirID);
        });
    }

    private moveFinish(
        success: boolean,
        id: number,
        parentID: number,
        instanceID: string,
        rootDirID = config.WIDGETS_ROOT_DIR_ID
    ) {
        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        delete instanceState.renameLoading[id];

        if (success !== true) {
            return;
        }

        //Move the same folder in all instances.
        _.each(this._state.data, (state, stateInstanceID) => {
            const { data } = state;
            const dirItem = data.dirsHash[id];

            if (dirItem == null || dirItem.parent == null) {
                return;
            }

            const index = _.indexOf(dirItem.parent.subdirlist, dirItem);
            dirItem.parent.subdirlist.splice(index, 1);

            const parentDirItem = data.dirsHash[parentID];
            if (parentDirItem != null) {
                dirItem.parent = parentDirItem;
                parentDirItem.subdirlist.push(dirItem);
            }

            const rootDirItem = this.findRecursive(data.dirsHash[rootDirID], (obj) => {
                return obj.dirid === rootDirID;
            });

            if (rootDirItem != null && rootDirItem.subdirlist) {
                state.data.dirs = Utilities.sortByName(rootDirItem.subdirlist);
            }
        });
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.DIRS_CONTENT_LOAD>(a, Constants.DIRS_CONTENT_LOAD)) {
                this.contentLoad(a.instanceId, a.rootId, a.rootName, a.force, a.silent);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_USER_GROUP_CONTENT_LOAD>(a, Constants.DIRS_USER_GROUP_CONTENT_LOAD)) {
                this.userGroupContentLoad(a.instanceId, a.rootId, a.rootName, a.force, a.silent);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_CONTENT_LOAD_FINISH>(a, Constants.DIRS_CONTENT_LOAD_FINISH)) {
                this.contentLoadFinish(a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_USER_GROUP_CONTENT_LOAD_FINISH>(a, Constants.DIRS_USER_GROUP_CONTENT_LOAD_FINISH)) {
                this.groupContentLoadFinish(a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_RENAME_START>(a, Constants.DIRS_RENAME_START)) {
                this.renameStart(a.id, a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_RENAME_END>(a, Constants.DIRS_RENAME_END)) {
                this.renameEnd(a.id, a.value, a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_CREATE_START>(a, Constants.DIRS_CREATE_START)) {
                this.createStart(a.parentID, a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_RENAME_FINISH>(a, Constants.DIRS_RENAME_FINISH)) {
                this.renameFinish(a.id, a.name, a.instanceID, a.isNew);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_DELETE>(a, Constants.DIRS_DELETE)) {
                this.delete(a.key, a.instanceId, a.deleteSubItemsToo);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_DELETE_FINISH>(a, Constants.DIRS_DELETE_FINISH)) {
                this.deleteFinish(a.success, a.id, a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_MOVE>(a, Constants.DIRS_MOVE)) {
                this.move(a.key, a.parentId, a.instanceId, a.rootDirId);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_MOVE_FINISH>(a, Constants.DIRS_MOVE_FINISH)) {
                this.moveFinish(a.success, a.key, a.parentId, a.instanceId, a.rootDirId);
                this.emitChange();
                break;
            }

            if (_case<I.DIRS_CLEAN_CACHE>(a, Constants.DIRS_CLEAN_CACHE)) {
                this.cleanCache();
                this.emitChange();
                break;
            }
        } while (false);

        return true;
    }

    constructor(glob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, DirsStore._name);

        this._state = _.extend(DirsStore.getInitialState(), this._state);
    }

    static get(glob, dispatcher: Dispatcher, actions: AppActions) {
        return new DirsStore(glob, dispatcher, actions);
    }
}
